import { useMutation, useQuery } from "./urqlHooks";

const entryLineCostCentersFragment = `
  costCenters {
    id
    costCenterId
    label
    debit
    credit
    currency
    createdBy
  }
`;

const reversedInformationFragment = `
  reversedInformation {
    reverseDate
    reverseCounterPartId
    userId
    userEmail
  }
`;

const devengadoInformationFragment = `
  devengadoInformation {
    devengoDate
    sourceEntryId
    devengoEntryIds
    reversalEntryIds
    index
    totalMonths
    userId
    userEmail
  }
`;

const CREATE_ENTRIES = `
  mutation CREATE_ENTRIES($input: [CreateEntryInput!]!, $companyId: String!) {
    createEntries(input: $input, companyId: $companyId) {
      id
      _id
      companyId
      entryNumber
      description
      stringDate
      entryDate {
        day
        month
        year
        fullDate
      }
      entryType
      emissionNature
      reversed
      ${reversedInformationFragment}
      ${devengadoInformationFragment}
      currency
      sources {
        type
        id
      }
      transactionId
      documentId
      entries {
        ranking
        accountKey
        accountName
        debit
        credit
        rut
        businessName
      }
      creationInformation {
        userId
        userEmail
        creationDate
      }
      editionHistory {
        date
        userId
        userEmail
        event
        fields {
          key
          value
          oldValue
        }
      }
    }
  }
`;

export const CreateEntries = () => {
  const [result, reexecuteMutation] = useMutation(CREATE_ENTRIES);
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteMutation };
};

const DEVENGAR_ENTRY = `
  mutation DEVENGAR_ENTRY($input: DevengarEntryInput!) {
    devengarEntry(input: $input) {
      id
      _id
      entryNumber
      stringDate
      entryType
    }
  }
`;

export const DevengarEntry = () => {
  const [result, reexecuteMutation] = useMutation(DEVENGAR_ENTRY);
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteMutation };
};

const GET_ENTRIES = `
  query GET_ENTRIES($companyId: String!) {
    entries(companyId: $companyId) {
      id
      _id
      companyId
      entryNumber
      description
      stringDate
      entryDate {
        day
        month
        year
        fullDate
      }
      entryType
      emissionNature
      reversed
      ${reversedInformationFragment}
      ${devengadoInformationFragment}
      currency
      sources {
        type
        id
      }
      transactionId
      documentId
      hasCostCenters
      entries {
        ranking
        accountKey
        accountName
        debit
        credit
        rut
        businessName
        ${entryLineCostCentersFragment}
      }
      creationInformation {
        userId
        userEmail
        creationDate
      }
    }
  }
`;

export const GetEntries = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_ENTRIES,
    variables,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_ENTRY_BY_ID = `
  query GET_ENTRY_BY_ID($companyId: String!, $entryId: String!) {
    entry(companyId: $companyId, entryId: $entryId) {
      id
      _id
      companyId
      entryNumber
      description
      stringDate
      entryDate {
        day
        month
        year
        fullDate
      }
      entryType
      emissionNature
      reversed
      ${reversedInformationFragment}
      ${devengadoInformationFragment}
      transactionId
      documentId
      sources {
        type
        id
      }
      hasCostCenters
      entries {
        ranking
        accountKey
        accountName
        debit
        credit
        rut
        businessName
        ${entryLineCostCentersFragment}
      }
      creationInformation {
        userId
        userEmail
        creationDate
      }
      editionHistory {
        date
        userId
        userEmail
        event
        fields {
          key
          value
          oldValue
        }
      }
    }
  }
`;

export const GetEntryById = (options) => {
  const { pause, ...variables } = options || {};
  const [result, reexecuteQuery] = useQuery({
    query: GET_ENTRY_BY_ID,
    variables,
    pause: pause !== undefined ? pause : false,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_ENTRIES_BY_IDS = `
  query GET_ENTRIES_BY_IDS($companyId: String!, $entryIds: [String!]!) {
    entriesByIds(companyId: $companyId, entryIds: $entryIds) {
      id
      _id
      companyId
      entryNumber
      description
      stringDate
      entryDate {
        day
        month
        year
        fullDate
      }
      entryType
      emissionNature
      reversed
      ${reversedInformationFragment}
      ${devengadoInformationFragment}
      transactionId
      documentId
      sources {
        type
        id
      }
      hasCostCenters
      entries {
        ranking
        accountKey
        accountName
        debit
        credit
        rut
        businessName
        ${entryLineCostCentersFragment}
      }
    }
  }
`;

export const GetEntriesByIds = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_ENTRIES_BY_IDS,
    variables,
    requestPolicy: "network-only",
    pause: !variables?.companyId || !variables?.entryIds?.length,
  });
  const { data, fetching, error } = result;
  return {
    data,
    fetching,
    error,
    reexecuteQuery,
    entries: data?.entriesByIds ?? [],
  };
};

const GET_ENTRY_BY_NUMBER = `
  query GET_ENTRY_BY_NUMBER($companyId: String!, $entryNumber: Int!) {
    entryByNumber(companyId: $companyId, entryNumber: $entryNumber) {
      id
      _id
      companyId
      entryNumber
      description
      stringDate
      entryDate {
        day
        month
        year
        fullDate
      }
      entryType
      emissionNature
      reversed
      ${reversedInformationFragment}
      ${devengadoInformationFragment}
      transactionId
      documentId
      entries {
        ranking
        accountKey
        accountName
        debit
        credit
        rut
        businessName
      }
      creationInformation {
        userId
        userEmail
        creationDate
      }
      editionHistory {
        date
        userId
        userEmail
        event
        fields {
          key
          value
          oldValue
        }
      }
    }
  }
`;

export const GetEntryByNumber = (variables, options = {}) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_ENTRY_BY_NUMBER,
    variables,
    ...options,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const MODIFY_ENTRY = `
  mutation MODIFY_ENTRY(
    $companyId: String!
    $input: [EntryToModify!]!
    $action: EventType
  ) {
    modifyEntry(
      companyId: $companyId
      input: $input
      action: $action
    ) {
      ok
      nModified
    }
  }
`;

export const ModifyEntry = () => {
  const [result, reexecuteMutation] = useMutation(MODIFY_ENTRY);
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteMutation };
};

const DELETE_ENTRY = `
  mutation DELETE_ENTRY($companyId: String!, $entriesId: [String!]!) {
    deleteEntry(companyId: $companyId, entriesId: $entriesId)
  }
`;

export const DeleteEntry = () => {
  const [result, reexecuteMutation] = useMutation(DELETE_ENTRY);
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteMutation };
};

export const GET_ENTRIES_BY_SOURCE = `
  query GET_ENTRIES_BY_SOURCE(
    $companyId: ID!
    $sourceId: ID!
    $sourceType: SourceType!
  ) {
    entriesBySource(
      companyId: $companyId
      sourceId: $sourceId
      sourceType: $sourceType
    ) {
      id
      _id
      companyId
      entryNumber
      description
      stringDate
      entryDate {
        day
        month
        year
        fullDate
      }
      entryType
      emissionNature
      reversed
      ${reversedInformationFragment}
      ${devengadoInformationFragment}
      currency
      sources {
        type
        id
      }
      transactionId
      documentId
      hasCostCenters
      entries {
        ranking
        accountKey
        accountName
        debit
        credit
        rut
        businessName
        ${entryLineCostCentersFragment}
      }
      creationInformation {
        userId
        userEmail
        creationDate
      }
      editionHistory {
        date
        userId
        userEmail
        event
        fields {
          key
          value
          oldValue
        }
      }
      isHidden
    }
  }
`;

export const GetEntriesBySource = (options) => {
  const { pause, ...variables } = options || {};
  const [result, reexecuteQuery] = useQuery({
    query: GET_ENTRIES_BY_SOURCE,
    variables,
    pause: pause !== undefined ? pause : false,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_EIGHT_COLUMN_BALANCE = `
  query GET_EIGHT_COLUMN_BALANCE($companyId: String!, $filters: EightColumnBalanceFilters) {
    getEightColumnBalance(companyId: $companyId, filters: $filters) {
      rows {
        accountKey
        name
        debit
        credit
        debtor
        creditor
        asset
        liability
        loss
        profit
      }
      totals {
        debit
        credit
        debtor
        creditor
        asset
        liability
        loss
        profit
      }
    }
  }
`;

export const GetEightColumnBalance = (variables, options = {}) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_EIGHT_COLUMN_BALANCE,
    variables,
    requestPolicy: 'network-only',
    ...options,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const blockFragment = `
  category { key label name values { fieldValue period documents } }
  rows {
    fieldKey field category categoryKey rank
    values { fieldValue period documents }
    costCenterSlices {
      costCenterId
      label
      values { fieldValue period entryIds }
    }
  }
`;

const GET_EERR_BY_ENTRY = `
  query GET_EERR_BY_ENTRY($input: GetEERRByEntryInput!) {
    getEERRByEntry(input: $input) {
      SALE { ${blockFragment.trim()} }
      COST { ${blockFragment.trim()} }
      GRASS_PROFIT { ${blockFragment.trim()} }
      EXPENSE { ${blockFragment.trim()} }
      OPERATING_PROFIT { ${blockFragment.trim()} }
      OTHER_EXPENSE { ${blockFragment.trim()} }
      NON_OPERATING_PROFIT { ${blockFragment.trim()} }
      TAXES { ${blockFragment.trim()} }
      NET_PROFIT { ${blockFragment.trim()} }
    }
  }
`;

export const GetEERRByEntry = (variables, options = {}) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_EERR_BY_ENTRY,
    variables,
    requestPolicy: 'network-only',
    pause: !variables?.input?.companyId,
    ...options,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_ENTRIES_BY_ACCOUNTS = `
  query GET_ENTRIES_BY_ACCOUNTS($input: GetEntriesByAccountsInput!) {
    getEntriesByAccounts(input: $input) {
      docs {
        id
        _id
        companyId
        entryNumber
        description
        stringDate
        entryDate {
          day
          month
          year
          fullDate
        }
        entryType
        emissionNature
        reversed
        ${reversedInformationFragment}
        ${devengadoInformationFragment}
      ${devengadoInformationFragment}
        currency
        transactionId
        documentId
        entries {
          ranking
          accountKey
          accountName
          debit
          credit
          rut
          businessName
        }
        creationInformation {
          userId
          userEmail
          creationDate
        }
        editionHistory {
          date
          userId
          userEmail
          event
          fields {
            key
            value
            oldValue
          }
        }
        isHidden
        accumulatedBalance
      }
      totalDocs
      next
      previous
    }
  }
`;

export const GetEntriesByAccounts = (variables, options = {}) => {
  const { pause: optionsPause, ...restOptions } = options;
  const defaultPause = !variables.input.accounts || variables.input.accounts.length === 0;
  const [result, reexecuteQuery] = useQuery({
    query: GET_ENTRIES_BY_ACCOUNTS,
    variables,
    requestPolicy: 'network-only',
    ...restOptions,
    pause: optionsPause || defaultPause,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_JOURNAL_ENTRIES = `
  query GET_JOURNAL_ENTRIES($input: GetJournalEntriesInput!) {
    getJournalEntries(input: $input) {
      docs {
        entryId
        stringDate
        entryNumber
        accountKey
        accountName
        debit
        credit
        description
      }
      totalDocs
      next
      previous
    }
  }
`;

export const GetJournalEntries = (variables, options = {}) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_JOURNAL_ENTRIES,
    variables,
    requestPolicy: 'network-only',
    ...options,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_ENTRIES_PAGINATED = `
  query GET_ENTRIES_PAGINATED($input: GetEntriesPaginatedInput!) {
    getEntriesPaginated(input: $input) {
      docs {
        id
        _id
        companyId
        entryNumber
        description
        stringDate
        entryDate {
          day
          month
          year
          fullDate
        }
        entryType
        emissionNature
        reversed
        ${reversedInformationFragment}
        ${devengadoInformationFragment}
      ${devengadoInformationFragment}
        currency
        transactionId
        documentId
        sources {
          type
          id
        }
        hasCostCenters
        entries {
          ranking
          accountKey
          accountName
          debit
          credit
          rut
          businessName
          ${entryLineCostCentersFragment}
        }
        creationInformation {
          userId
          userEmail
          creationDate
        }
        editionHistory {
          date
          userId
          userEmail
          event
          fields {
            key
            value
            oldValue
          }
        }
        isHidden
      }
      totalDocs
      next
      previous
    }
  }
`;

export const GetEntriesPaginated = (options) => {
  const { pause, ...variables } = options || {};
  const [result, reexecuteQuery] = useQuery({
    query: GET_ENTRIES_PAGINATED,
    variables,
    requestPolicy: 'network-only',
    pause: pause !== undefined ? pause : false,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_CLASSIFIED_BALANCE = `
  query GET_CLASSIFIED_BALANCE($input: GetClassifiedBalanceInput!) {
    getClassifiedBalance(input: $input) {
      activo {
        name
        accounts {
          accountKey
          name
          amount
          categoryName
        }
        total
      }
      pasivo {
        name
        accounts {
          accountKey
          name
          amount
          categoryName
        }
        total
      }
      totalActivo
      totalPasivo
      perdidaUtilidad
    }
  }
`;

export const GetClassifiedBalance = (variables, options = {}) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_CLASSIFIED_BALANCE,
    variables,
    requestPolicy: 'network-only',
    ...options,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_LIBRO_MAYOR_EXCEL_FILE = `
  query GET_LIBRO_MAYOR_EXCEL_FILE($input: GetLibroMayorExcelFileInput!) {
    getLibroMayorExcelFile(input: $input)
  }
`;

export const GetLibroMayorExcelFile = (variables, options = {}) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_LIBRO_MAYOR_EXCEL_FILE,
    variables,
    requestPolicy: 'network-only',
    pause: options.pause !== undefined ? options.pause : false,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

